






































































import Vue from "vue";

import JzMicController from "./JzMicController.vue";
import JzMicMsg from "./JzMicMsg.vue";
import JzMicTextBox from "./JzMicTextBox.vue";
import JzSpinner from "../feedback/JzSpinner.vue";

import { MicConfig, buildMic } from "../../utils/microphone";
import { MicHandler, MicErrorKey } from "../../models/microphone";
import { MIC_DFLT_MSG } from "../../constants/microphone";

type InputDisplay =
  | "init-error"
  | "unknown"
  | "permission"
  | "controller"
  | "spinner";

type ErrorMsgs = { [key in MicErrorKey]?: string };

const DLFT_TIMEOUT = 15000;

interface Data {
  emittedErrors: string[];
  mic: MicHandler;
  micTimeoutChecker?: number;
  micTimeout: boolean;
}

interface Computed {
  showDotDotDot: boolean;
  isDebug: boolean;
  display: InputDisplay;
  msgInfo?: string;
  msgErrorInit?: string;
  msgErrorRuntime?: string;
  micLogs: string[];
  showTextBox: boolean;
}

interface Props {
  // To have an error message displayed in the error but this error is not
  // related the microphone functionalities themselves but rather a logic
  // error happening out of the microphone scope
  customError: string;
  disabled: boolean;
  hideTextBox: boolean;
  // TODO: find a better way to handle different positioning
  infoAtBottom: boolean;
  loading: boolean;
  micConfig: MicConfig;
  micTimeoutThreshold: number;
  // A generic error message for runtime
  msgErrRuntime: string;
  // A generic error message for initialisation
  msgErrInit: string;
  // A detailed error messages list
  msgErrors: ErrorMsgs;
  msgMicPermission: string;
  msgPressToStop: string;
  msgTapToRecord: string;
  showSilenceAlert: boolean;
}

interface Methods {
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  deleteRecording: () => Promise<void>;
  resetData: () => void;
  sendRecording: () => void;
  reinitMic: () => Promise<void>;
}

/**
 * @emits mic-start when the user clicks the start recording button
 * @emits mic-stop when the user clicks the stop recording button
 * @emits mic-delete when the user clicks the "Delete All" button
 * @emits mic-send(this.mic.micData) when the user clicks the "Send" button
 * @emits mic-timeout
 */
export default Vue.extend<Data, Methods, Computed, Props>({
  name: "JzMic",
  components: { JzMicController, JzMicMsg, JzMicTextBox, JzSpinner },

  props: {
    customError: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    hideTextBox: { type: Boolean, default: false },
    infoAtBottom: { type: Boolean, default: false },
    micConfig: { type: Object as () => MicConfig, required: true },
    micTimeoutThreshold: { type: Number, default: DLFT_TIMEOUT },
    msgErrRuntime: { type: String, default: MIC_DFLT_MSG.ERROR_RUNTIME },
    msgErrInit: { type: String, default: MIC_DFLT_MSG.ERROR_INIT },
    msgErrors: {
      type: Object as () => ErrorMsgs,
      default: () => ({})
    },
    msgMicPermission: { type: String, default: MIC_DFLT_MSG.ALLOW_PERMISSION },
    msgPressToStop: { type: String, default: MIC_DFLT_MSG.PRESS_TO_STOP },
    msgTapToRecord: { type: String, default: MIC_DFLT_MSG.TAP_TO_RECORD },
    showSilenceAlert: { type: Boolean, default: false }
  },

  data(): Data {
    return {
      emittedErrors: ["mic-error_microsoft-sdk_connection-error"],
      mic: buildMic(this.micConfig),
      micTimeoutChecker: undefined,
      micTimeout: false
    };
  },

  computed: {
    showDotDotDot(): boolean {
      return this.mic.status === "recording" || this.mic.isProcessing;
    },
    isDebug() {
      return this.micConfig.options.debug || false;
    },

    msgInfo() {
      if (this.loading) {
        return undefined;
      }
      // Steady, ready to record
      else if (this.mic.status === "ready") {
        return this.msgTapToRecord;
      }
      // Explain how to stop recoridng
      else if (this.mic.status === "recording") {
        return this.msgPressToStop;
      }

      return undefined;
    },

    msgErrorInit() {
      if (this.mic.status !== "init-err") {
        return undefined;
      }

      if (!this.mic.error) {
        return this.msgErrorInit;
      }

      const errMsg: string | undefined = this.msgErrors[this.mic.error.key];
      return errMsg !== undefined ? errMsg : this.msgErrInit;
    },

    msgErrorRuntime(): string | undefined {
      // Custom errors should not override a microphone error.
      let errorRuntime = "";
      if (this.customError !== "") {
        errorRuntime += `${this.customError} `;
      }

      if (!this.mic.error || this.emittedErrors.includes(this.mic.error.key)) {
        return errorRuntime;
      }

      const errMsg: string | undefined = this.msgErrors[this.mic.error.key];
      errorRuntime += errMsg !== undefined ? errMsg : this.msgErrRuntime;

      return errorRuntime;
    },

    display(): InputDisplay {
      if (this.loading) {
        return "spinner";
      }

      // Error display
      if (this.mic.status === "init-err") {
        return "init-error";
      }

      // Spinner
      if (this.mic.isProcessing) {
        return "spinner";
      }

      // Wait for permission
      if (this.mic.status === "permission") {
        return "permission";
      }

      return "controller";
    },

    micLogs() {
      return this.mic.logMsgs.slice().reverse();
    },

    showTextBox() {
      // Never show the text box for the plain audio recorder
      if (this.micConfig.impl === "ScriptNodeRecorder") {
        return false;
      }

      return !this.hideTextBox;
    }
  },

  watch: {
    "mic.isProcessing": function(newVal) {
      // Processing started
      if (newVal) {
        if (this.isDebug) {
          const l = `JzMic: start timeout check of ${this.micTimeoutThreshold}ms`;
          console.debug(l);
        }

        this.micTimeoutChecker = window.setTimeout(() => {
          // Cancel is not fully functional and only prevent infinite loops from happening
          this.mic.cancel();
          this.micTimeout = true;

          this.$emit("mic-timeout");
        }, this.micTimeoutThreshold);
      }
      // Processing ended
      else {
        window.clearTimeout(this.micTimeoutChecker);
      }
    },
    "mic._errors": function(errors) {
      if (errors.length <= 0) {
        return;
      }
      const lastError = errors[errors.length - 1];

      const connectionError = {
        key: "mic-error_microsoft-sdk_connection-error"
      };

      if (lastError.key === connectionError.key) {
        this.$emit("connection-error", connectionError);
      }
    }
  },

  created() {
    this.mic.init?.();
  },

  methods: {
    async startRecording() {
      this.$emit("mic-start");

      this.micTimeout = false;

      await this.mic.start();
    },

    async stopRecording() {
      this.$emit("mic-stop");

      await this.mic.stop();
    },

    async deleteRecording() {
      this.micTimeout = false;

      await this.mic.delete();
      this.$emit("mic-delete");
    },

    resetData() {
      this.mic.resetMicData();
    },

    sendRecording() {
      this.$emit("mic-send", this.mic.micData);
    },

    /**
     * Re-initialise the microphone instance, with potentially a modified
     * configuration
     *
     * Note: when MediaStream permission is missing, reloading page with
     * `window.location.reload();` and re-requesting the getUserMedia does
     * not work
     */
    async reinitMic() {
      this.mic = buildMic(this.micConfig);
      await this.mic.init?.();
    }
  }
});
